import React, { useEffect, useRef } from 'react';

import { createSearchParams, useNavigate } from 'react-router';

import { enqueueSnackbar } from 'notistack';

import { useApplicationContext } from 'context/ApplicationContext';
import { usePedidoCompraContext } from 'context/PedidoCompraContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { useShowError } from 'hooks/useShowError';
import { useFormMutation } from 'mutations/useFormMutation';
import { FinalizarFormData } from 'pages/compra/pedido/types';

type UsePostPedido = {
    handleSubmit: () => void;
    isLoading: boolean;
};

export function usePostPedido(): UsePostPedido {
    const { showError } = useShowError();
    const navigate = useNavigate();
    const { mutate, isLoading } = useFormMutation();
    const { setIsSavingCarrinho, configPedido, loja, idPedido, tipoEntrega, produtoCartList, funcionario, cdIdempotency, setCdIdempotency } =
        usePedidoCompraContext();
    const { hasPermission } = usePermissionsContext();
    const { user } = useApplicationContext();
    const timesClicked = useRef(0);
    const hasPermissionFinalizacaoGeral = hasPermission(['COMPRA_PEDIDO_FINALIZACAO_GERAL']);

    useEffect(() => setIsSavingCarrinho(isLoading), [isLoading]);

    const getFinalizarFormData = (): FinalizarFormData | undefined => {
        if (!configPedido || !loja) {
            return;
        }

        return {
            idPedido,
            produtos: produtoCartList.map((produto) => {
                return { qtProduto: produto.qtProdutoCart, idProduto: produto.produto.idProduto };
            }),
            configPedido: {
                idConfigPedido: configPedido.idConfigPedido,
            },
            loja: {
                idLoja: loja?.idLoja,
            },
            ...(tipoEntrega && { tipoEntrega: { idTipoEntrega: tipoEntrega?.idTipoEntrega } }),
            ...(funcionario && { usuarioPedido: { idUsuario: funcionario?.idUsuario } }),
        };
    };

    const navigateToCarrinho = () =>
        navigate({
            pathname: '/compra/carrinho',
            search: createSearchParams({
                idLoja: JSON.stringify([loja]),
                ...((funcionario || user) && hasPermissionFinalizacaoGeral && { idUsuarioPedido: JSON.stringify(funcionario || user) }),
            }).toString(),
        });

    const sendInfoPedidoDuplicado = (error: any): void => {
        mutate({
            url: '/gestao/pedidologerro/gravar',
            preventSnack: true,
            formData: {
                body: error?.config?.data,
                agent: window.navigator.userAgent,
                timestamp: error?.data?.timestamp,
                timesClicked: timesClicked.current,
            },
        });
    };

    const handleSubmit = (): void => {
        if (!configPedido || !loja) {
            return;
        }

        timesClicked.current += 1;

        mutate(
            {
                cdIdempotency,
                idField: 'idPedido',
                url: '/compra/pedidowrapper/rascunho',
                preventSnack: true,
                preventRedirect: true,
                formData: getFinalizarFormData(),
            },
            {
                onSuccess: () => {
                    enqueueSnackbar('Pedido salvo com sucesso!', { variant: 'success' });

                    setCdIdempotency(null);

                    navigateToCarrinho();
                },
                onError: (error: any) => {
                    // 409 = Pedido duplicado
                    if (error?.status && error?.status === 409) {
                        sendInfoPedidoDuplicado(error);
                    } else {
                        showError(error, 'Ocorreu uma falha ao salvar o pedido, tente novamente');
                    }
                },
            },
        );
    };

    return { handleSubmit, isLoading };
}
